import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-input is a token-styled text control. It emits a composed `input` event and
 * mirrors the value on the `value` property, so pages bind it like a native
 * input: `.value=${x} @input=${(e) => x = e.target.value}`. Pair it with
 * bn-field for a label + hint + error, or use `invalid` on its own.
 */
let uid = 0;

@customElement("bn-input")
export class BnInput extends LitElement {
  @property() value = "";
  @property() type = "text";
  @property() name = "";
  @property() placeholder = "";
  @property() autocomplete = "";
  @property() inputmode = "";

  /** Stable id/name for the internal control so it isn't an anonymous form field. */
  private readonly fieldId = `bn-input-${++uid}`;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) invalid = false;
  @property({ type: Boolean }) mono = false;

  static override styles = css`
    :host {
      display: block;
    }
    input {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      transition:
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    input.mono {
      font-family: var(--bino-font-mono);
    }
    input::placeholder {
      color: var(--bino-text-secondary);
    }
    input:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    input.invalid {
      border-color: var(--bino-error);
    }
    input.invalid:focus {
      box-shadow: 0 0 0 3px var(--bino-error-bg);
    }
    input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `;

  private onInput(e: Event) {
    // The native `input` event is composed, so it would otherwise escape the
    // shadow root and reach the page's own `@input` handler alongside the
    // CustomEvent below — but with `detail` = 0, so `e.detail.value` is
    // undefined and clobbers the page's bound state (rendering the literal
    // "undefined" and making the field impossible to type in). Stop the native
    // event at the boundary so consumers only ever see our `{value}` event.
    e.stopPropagation();
    this.value = (e.target as HTMLInputElement).value;
    this.dispatchEvent(
      new CustomEvent("input", { detail: { value: this.value }, bubbles: true, composed: true }),
    );
  }

  override render() {
    return html`
      <input
        class="${this.invalid ? "invalid" : ""} ${this.mono ? "mono" : ""}"
        id=${this.fieldId}
        name=${this.name || this.fieldId}
        type=${this.type}
        .value=${this.value}
        placeholder=${this.placeholder}
        autocomplete=${this.autocomplete || "off"}
        inputmode=${this.inputmode || "text"}
        ?disabled=${this.disabled}
        @input=${this.onInput}
      />
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-input": BnInput;
  }
}
